import { Image, Select, Stack } from '@mantine/core'
import { IconTrophy } from '@tabler/icons-react'
import { z } from 'zod'
import { getRankSuffix } from '../../../../common/helpers'
import { useCompetitionId } from '../../../../common/hooks'
import { useAllPlayers } from '../../../../players/api'
import { useAllTeams } from '../../../../teams/api'
import { useMetrics, useMetricsData } from '../../../../widgets/api'
import { useFmt, useFmtColor } from '../../../../widgets/format'
import {
  ColorSchemaFields,
  MetricSelect,
  NeutralColorsToggle,
  TitleInput,
} from './OptionsFormFields'
import { WidgetEmpty, WidgetSuspense } from './WidgetShell'
import classes from './Top5Kpi.module.css'
import { DashboardWidget } from './types'

const TOP_N = 5

// The "total" period_type rows are generated with a fixed period date in the
// backend (see api/src/metrics/queries/generate_context.sql) — passing any
// other date returns no rows.
const TOTAL_PERIOD = '2025-01-01'

const Schema = z.object({
  ...ColorSchemaFields,
  title: z.string().default(''),
  metric_key: z.string().default(''),
  subject_type: z.enum(['team', 'user']).default('team'),
  mode: z.enum(['achievement', 'contribution']).default('achievement'),
})

type Settings = z.infer<typeof Schema>

type Entry = {
  subject_id: number
  name: string
  logo_url: string
  value: number
}

function ValueLabel({
  metricKey,
  component,
  value,
}: {
  metricKey: string
  component: 'percent' | 'actual'
  value: number
}) {
  const formatted = useFmt({
    metric: metricKey,
    component,
    statistic: 'value',
    value,
  })
  return <div className={classes.percent}>{formatted}</div>
}

function Card({
  entry,
  rank,
  metricKey,
  maxValue,
  neutral,
  component,
}: {
  entry: Entry
  rank: number
  metricKey: string
  maxValue: number
  neutral: boolean
  component: 'percent' | 'actual'
}) {
  // Achievement (percent) mode: the bar shows each subject's own % of target on
  // an absolute 0–100 scale (>100% just fills the bar). Contribution mode: the
  // bar is relative to the top contributor, so the leader fills it and everyone
  // else is proportional.
  const fill =
    component === 'percent'
      ? Math.min(100, entry.value * 100)
      : maxValue > 0
        ? Math.min(100, (entry.value / maxValue) * 100)
        : 0
  // Only the colour is metric-driven, so it matches the threshold colours used
  // by the other KPI widgets. In neutral mode — or contribution mode, where the
  // bar shows absolute actuals and the percent thresholds are meaningless — we
  // drop the colour and let the CSS-module teal default apply.
  const thresholdColor = useFmtColor({
    metric: metricKey,
    component: 'percent',
    statistic: 'value',
    value: entry.value,
  })
  const barColor =
    neutral || component === 'actual' ? undefined : thresholdColor
  return (
    <div className={classes.card}>
      <div className={classes.rank}>
        {rank}
        <span className={classes.rankSuffix}>{getRankSuffix(rank)}</span>
      </div>
      <Image
        src={entry.logo_url}
        alt={entry.name}
        fit="contain"
        className={classes.logo}
      />
      <div className={classes.right}>
        <div className={classes.name}>{entry.name}</div>
        <div className={classes.barRow}>
          <div className={classes.barTrack}>
            <div
              className={classes.barFill}
              style={{
                width: `${fill}%`,
                ...(barColor ? { backgroundColor: barColor } : {}),
              }}
            />
          </div>
          <ValueLabel
            metricKey={metricKey}
            component={component}
            value={entry.value}
          />
        </div>
      </div>
    </div>
  )
}

function TeamInner({
  metricKey,
  metricLabel,
  title,
  neutral,
  component,
}: {
  metricKey: string
  metricLabel: string
  title: string
  neutral: boolean
  component: 'percent' | 'actual'
}) {
  const competitionId = useCompetitionId()
  const { data: teamsResp } = useAllTeams({ competition_id: competitionId })
  const teams = teamsResp.items

  const subjectIds = teams.map((t) => t.team_id)
  const { data: dataPoints } = useMetricsData({
    period_type: 'total',
    period_range: false,
    periods: [TOTAL_PERIOD],
    subject_type: 'team',
    subject_ids: subjectIds.length > 0 ? subjectIds : [0],
    metrics: subjectIds.map(() => metricKey),
    components: subjectIds.map(() => component),
    statistics: subjectIds.map(() => 'value'),
    competition_id: competitionId,
  })

  const valueBySubject = new Map<number, number>()
  for (const p of dataPoints) valueBySubject.set(p.subject, p.value)

  const entries: Entry[] = teams.flatMap((t) => {
    const v = valueBySubject.get(t.team_id)
    if (v == null) return []
    return [
      {
        subject_id: t.team_id,
        name: t.name,
        logo_url: t.logo.thumbnail,
        value: v,
      },
    ]
  })
  entries.sort((a, b) => b.value - a.value)
  const top = entries.slice(0, TOP_N)

  if (top.length === 0) {
    return <WidgetEmpty message={`No ${metricLabel} data yet`} />
  }

  const maxValue = top[0].value
  return (
    <Layout title={title}>
      {top.map((entry, i) => (
        <Card
          key={entry.subject_id}
          entry={entry}
          rank={i + 1}
          metricKey={metricKey}
          maxValue={maxValue}
          neutral={neutral}
          component={component}
        />
      ))}
    </Layout>
  )
}

function UserInner({
  metricKey,
  metricLabel,
  title,
  neutral,
  component,
}: {
  metricKey: string
  metricLabel: string
  title: string
  neutral: boolean
  component: 'percent' | 'actual'
}) {
  const competitionId = useCompetitionId()
  const { data: playersResp } = useAllPlayers({ competition_id: competitionId })
  const players = playersResp.items.filter((p) => p.user_id != null)

  const subjectIds = players.map((p) => p.user_id as number)
  const { data: dataPoints } = useMetricsData({
    period_type: 'total',
    period_range: false,
    periods: [TOTAL_PERIOD],
    subject_type: 'user',
    subject_ids: subjectIds.length > 0 ? subjectIds : [0],
    metrics: subjectIds.map(() => metricKey),
    components: subjectIds.map(() => component),
    statistics: subjectIds.map(() => 'value'),
    competition_id: competitionId,
  })

  const valueBySubject = new Map<number, number>()
  for (const p of dataPoints) valueBySubject.set(p.subject, p.value)

  const entries: Entry[] = players.flatMap((p) => {
    const userId = p.user_id as number
    const v = valueBySubject.get(userId)
    if (v == null) return []
    return [
      {
        subject_id: userId,
        name: p.name,
        logo_url: p.photo.thumbnail,
        value: v,
      },
    ]
  })
  entries.sort((a, b) => b.value - a.value)
  const top = entries.slice(0, TOP_N)

  if (top.length === 0) {
    return <WidgetEmpty message={`No ${metricLabel} data yet`} />
  }

  const maxValue = top[0].value
  return (
    <Layout title={title}>
      {top.map((entry, i) => (
        <Card
          key={entry.subject_id}
          entry={entry}
          rank={i + 1}
          metricKey={metricKey}
          maxValue={maxValue}
          neutral={neutral}
          component={component}
        />
      ))}
    </Layout>
  )
}

function Layout({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className={classes.wrapper}>
      <div className={classes.title}>{title}</div>
      <div className={classes.cards}>{children}</div>
    </div>
  )
}

function Inner({ settings }: { settings: Settings }) {
  const { data: allMetrics } = useMetrics()
  const metricKey =
    settings.metric_key ||
    allMetrics.find((m) => m.is_kpi)?.key ||
    allMetrics[0]?.key ||
    ''
  const metric = allMetrics.find((m) => m.key === metricKey)
  if (!metric) return <WidgetEmpty message="No metric available" />

  const metricLabel = metric.short_name || metric.name
  const title = settings.title || `Top 5: ${metricLabel}`
  const component = settings.mode === 'contribution' ? 'actual' : 'percent'

  if (settings.subject_type === 'user') {
    return (
      <UserInner
        metricKey={metricKey}
        metricLabel={metricLabel}
        title={title}
        neutral={settings.use_neutral_colors}
        component={component}
      />
    )
  }
  return (
    <TeamInner
      metricKey={metricKey}
      metricLabel={metricLabel}
      title={title}
      neutral={settings.use_neutral_colors}
      component={component}
    />
  )
}

function Component({ settings }: { settings: Settings }) {
  return (
    <WidgetSuspense>
      <Inner settings={settings} />
    </WidgetSuspense>
  )
}

function OptionsForm({
  value,
  onChange,
}: {
  value: Settings
  onChange: (next: Settings) => void
}) {
  const patch = (p: Partial<Settings>) => onChange({ ...value, ...p })
  return (
    <Stack gap="xs">
      <TitleInput value={value.title} onChange={(v) => patch({ title: v })} />
      <MetricSelect
        kpiOnly
        value={value.metric_key}
        onChange={(v) => patch({ metric_key: v })}
      />
      <Select
        label="Show top"
        data={[
          { value: 'team', label: 'Teams' },
          { value: 'user', label: 'Players' },
        ]}
        value={value.subject_type}
        onChange={(v) =>
          patch({ subject_type: (v as 'team' | 'user') || 'team' })
        }
      />
      <Select
        label="Display"
        data={[
          { value: 'achievement', label: 'Achievement (% of target)' },
          { value: 'contribution', label: 'Contribution (actuals)' },
        ]}
        value={value.mode}
        onChange={(v) =>
          patch({
            mode: (v as 'achievement' | 'contribution') || 'achievement',
          })
        }
      />
      <NeutralColorsToggle
        value={value.use_neutral_colors}
        onChange={(v) => patch({ use_neutral_colors: v })}
      />
    </Stack>
  )
}

export const Top5KpiWidget: DashboardWidget<Settings> = {
  type: 'top5_kpi',
  label: 'Top 5 KPI',
  description: 'Top 5 teams or players for one KPI metric',
  icon: IconTrophy,
  minW: 2,
  minH: 3,
  maxW: 3,
  maxH: 3,
  schema: Schema,
  defaultSettings: Schema.parse({}),
  Component,
  OptionsForm,
}
